import { useState } from 'react'
import { useQuery, useMutation, usePaginatedQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { useAuth, usePermissions } from '@/hooks/useAuth'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Loading } from '@/components/ui/loading'
import { 
  List, 
  Plus, 
  Search, 
  Edit, 
  Trash2,
  User,
  Building,
  ChevronDown,
  Loader2
} from 'lucide-react'
import {Link, useSearchParams} from 'react-router-dom'
import {Id} from "../../../convex/_generated/dataModel";
import { ExternalCatalog } from '@/components/externalLabs/ExternalCatalog'
import { LaboratoryTabs, useLaboratoryTab } from '@/components/externalLabs/LaboratoryTabs'
import {
  inferLegacyPricingMode,
  type PricingMode,
} from '../../../convex/lib/applicationType'

const ITEMS_PER_PAGE = 10

export default function ListinoList() {
  const { profile } = useAuth()
  const { hasPermission } = usePermissions()
  const primoLab = hasPermission('view_listino')
  const external = hasPermission('view_external_prescriptions') || hasPermission('manage_external_laboratories')
  const { tab, setTab } = useLaboratoryTab(primoLab)
  const showExternal = external && tab === 'external'
  return (
    <div className="space-y-6">
      <LaboratoryTabs value={showExternal ? 'external' : 'primolab'} onChange={setTab} primoLab={primoLab} external={external} />
      {showExternal ? <ExternalCatalog key={profile?.company_id} /> : primoLab ? <PrimoLabListinoList /> : null}
    </div>
  )
}

function PrimoLabListinoList() {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { profile } = useAuth()
  const { hasPermission } = usePermissions()
  const [searchTerm, setSearchTerm] = useState('')
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedOrganizationId = searchParams.get('organizationId') || ''
  const selectedCompanyId = selectedOrganizationId ? '' : searchParams.get('companyId') || ''
  const [applicationSetupMode, setApplicationSetupMode] = useState(false)
  const [applicationFilter, setApplicationFilter] = useState<'missing' | 'all'>('missing')
  const [savingApplicationId, setSavingApplicationId] = useState<string | null>(null)
  const [applicationFeedback, setApplicationFeedback] = useState<string | null>(null)

  // Check permissions
  const canManageListino = hasPermission('manage_listino')
  const canViewPrices = hasPermission('view_prices')
  const canManageOrganizationListino = canManageListino && (profile?.ruolo === 'amministratore' || profile?.ruolo === 'tecnico_lab')
  const canManagePhases =
    hasPermission('manage_company_phases') ||
    hasPermission('manage_phase_policies')
  const canViewListino = hasPermission('view_listino')
  const canViewCompanyUsers = hasPermission('view_company_users')
  const canViewCompanyData = hasPermission('view_company_data') ||
    hasPermission('view_companies') || hasPermission('manage_companies')

  // Get listino items with pagination
  const {
    results: listinoItems,
    status: listinoStatus,
    loadMore: loadMoreListino
  } = usePaginatedQuery(
    api.listino.listListino, 
    {
      company_id: canManageListino && selectedCompanyId ? selectedCompanyId as Id<"companies">: undefined,
      organization_id: canManageOrganizationListino && selectedOrganizationId ? selectedOrganizationId as Id<"organizations"> : undefined
    },
    { initialNumItems: ITEMS_PER_PAGE }
  )

  const hasSearch = Boolean(searchTerm)

  // Full dataset for accurate stats and search across all listino items
  const allListinoItems = useQuery(
    api.listino.listListino,
    canViewListino ? {
      company_id: canManageListino && selectedCompanyId ? selectedCompanyId as Id<"companies">: undefined,
      organization_id: canManageOrganizationListino && selectedOrganizationId ? selectedOrganizationId as Id<"organizations"> : undefined
    } : "skip"
  )
  
  // Get users and companies for display conditionally (not paginated - usually few items)
  const users = useQuery(
    api.profiles.listProfiles,
    canViewCompanyUsers && profile
      ? {
          paginationOpts: { numItems: 100, cursor: null },
          viewerKey: profile._id,
        }
      : 'skip'
  )
  const companies = useQuery(
    api.companies.listCompanies, 
    canViewCompanyData ? {} : "skip"
  )

  const organizations = useQuery(api.organizations.listOrganizations, canViewCompanyData ? {} : 'skip')
  const newItemPath = selectedOrganizationId
    ? `/listino/new?organizationId=${selectedOrganizationId}`
    : selectedCompanyId ? `/listino/new?companyId=${selectedCompanyId}` : '/listino/new'

  // Delete listino item mutation
  const deleteListinoItem = useMutation(api.listino.deleteListinoItem)
  const updateListinoItem = useMutation(api.listino.updateListinoItem)

  if (!canViewListino) {
    return (
      <div className="py-8">
        <Card>
          <CardContent className="p-8 text-center">
            <List className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h2 className="text-xl font-semibold mb-2">Accesso Negato</h2>
            <p className="text-gray-600">Non hai i permessi per visualizzare il listino.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (listinoStatus === 'LoadingFirstPage' || (users === undefined && canViewCompanyUsers) || (canViewCompanyData && (companies === undefined || organizations === undefined))) {
    return <Loading />
  }

  if (allListinoItems === undefined) {
    return <Loading />
  }

  // Create user and company lookup
  const userLookup = users?.page?.reduce((acc, user) => {
    acc[user._id] = user
    return acc
  }, {} as Record<string, any>) || {}

  const companyRows = Array.isArray(companies) ? companies : companies?.page ?? []
  const companyLookup = companyRows.reduce((acc, company) => {
    acc[company._id] = company
    return acc
  }, {} as Record<string, any>) || {}

  const organizationForCompany = (companyId?: string) => {
    const organizationId = companyId ? companyLookup[companyId]?.organization_id : undefined
    return organizations?.find((organization) => organization._id === organizationId)
  }
  const isSuspended = (item: any) => !item.organization_id &&
    Boolean(organizationForCompany(item.company_id)?.share_price_lists)
  const contextCompanyId = canManageListino ? selectedCompanyId : profile?.company_id
  const contextCompany = contextCompanyId ? companyLookup[contextCompanyId] : undefined
  const contextOrganization = organizationForCompany(contextCompanyId)
  const selectedOrganization = organizations?.find((organization) => organization._id === selectedOrganizationId)

  const baseItems = applicationSetupMode || hasSearch
    ? (allListinoItems as any[] | undefined)
    : listinoItems

  // Filter listino items based on search
  const filteredItems = (baseItems as any)?.filter((item: any) => {
    const matchesSearch =
      item.nome_prestazione.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.nome_visualizzato?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.descrizione?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesApplication =
      !applicationSetupMode ||
      applicationFilter === 'all' ||
      !item.pricing_mode
    return matchesSearch && matchesApplication
  }) || []

  const missingApplicationCount = (allListinoItems as any[]).filter(
    (item: any) => !item.pricing_mode
  ).length
  const showApplicationColumn = canManageListino && applicationSetupMode

  const handleApplicationChange = async (item: any, pricingMode: PricingMode) => {
    setSavingApplicationId(item._id)
    setApplicationFeedback(null)
    try {
      await updateListinoItem({
        listino_id: item._id,
        pricing_mode: pricingMode,
      })
      const labels: Record<PricingMode, string> = {
        per_tooth: 'Per dente',
        per_arch: 'Per arcata',
        per_mouth: 'Per bocca',
      }
      setApplicationFeedback(
        `${item.nome_visualizzato || item.nome_prestazione}: ${labels[pricingMode]}`
      )
    } catch (error) {
      console.error('Errore aggiornamento applicazione:', error)
      setApplicationFeedback('Errore durante il salvataggio dell’applicazione.')
    } finally {
      setSavingApplicationId(null)
    }
  }

  const handleDeleteItem = async (itemId: string) => {
    if (window.confirm('Sei sicuro di voler eliminare questo elemento del listino?')) {
      try {
        await deleteListinoItem({ listino_id: itemId })
      } catch (error) {
        console.error('Errore eliminazione elemento listino:', error)
        alert('Errore durante l\'eliminazione dell\'elemento del listino')
      }
    }
  }

  const formatPrice = (price: number) => {
    if (!canViewPrices || !Number.isFinite(price)) return '—'
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(price)
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 border-b border-gray-200 pb-5 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <List className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-[-0.02em] text-gray-950">
              Gestione listino
            </h1>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-gray-600">
              Gestisci le prestazioni, le assegnazioni e i prezzi del listino.
            </p>
          </div>
        </div>
        {canManageListino && (
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant={applicationSetupMode ? 'default' : 'outline'}
              className="min-h-11 w-full sm:w-auto"
              onClick={() => {
                setApplicationSetupMode((value) => !value)
                setApplicationFilter('missing')
                setApplicationFeedback(null)
              }}
            >
              {applicationSetupMode ? 'Chiudi configurazione' : 'Configura applicazioni'}
            </Button>
            <Link to={newItemPath} className="w-full sm:w-auto">
              <Button className="min-h-11 w-full sm:w-auto">
                <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />
                <span>Nuova Prestazione</span>
              </Button>
            </Link>
          </div>
        )}
      </div>

      <Card>
        <CardHeader className="space-y-5 pb-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-lg">Prestazioni</CardTitle>
              <p className="mt-1 text-sm text-gray-600">
                Cerca una voce e gestisci configurazione, prezzo e disponibilità.
              </p>
            </div>
            <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700 tabular-nums">
              {filteredItems.length} di {(allListinoItems as any[]).length}
            </span>
          </div>

          <dl className="grid grid-cols-2 overflow-hidden rounded-lg border border-gray-200 bg-gray-50/70 sm:grid-cols-4">
            <div className="border-b border-r border-gray-200 px-4 py-3 sm:border-b-0">
              <dt className="text-xs font-medium text-gray-500">Totali</dt>
              <dd className="mt-0.5 text-base font-semibold text-gray-950 tabular-nums">
                {(allListinoItems as any[]).length}
              </dd>
            </div>
            <div className="border-b border-gray-200 px-4 py-3 sm:border-b-0 sm:border-r">
              <dt className="text-xs font-medium text-gray-500">Attive non sospese</dt>
              <dd className="mt-0.5 text-base font-semibold text-gray-950 tabular-nums">
                {(allListinoItems as any[]).filter(
                  (item: any) => item.is_active && !item.is_deleted && !isSuspended(item)
                ).length}
              </dd>
            </div>
            <div className="border-r border-gray-200 px-4 py-3">
              <dt className="text-xs font-medium text-gray-500">Prezzo medio</dt>
              <dd className="mt-0.5 whitespace-nowrap text-base font-semibold text-gray-950 tabular-nums">
                {(allListinoItems as any[]).length > 0
                  ? formatPrice(
                      (allListinoItems as any[]).reduce(
                        (sum: number, item: any) => sum + item.prezzo,
                        0
                      ) / (allListinoItems as any[]).length
                    )
                  : '€0'}
              </dd>
            </div>
            <div className="px-4 py-3">
              <dt className="text-xs font-medium text-gray-500">Risultati</dt>
              <dd className="mt-0.5 text-base font-semibold text-gray-950 tabular-nums">
                {filteredItems.length}
              </dd>
            </div>
          </dl>

          <div
            className={`grid gap-3 ${
              canManageListino
                ? 'sm:grid-cols-2 lg:grid-cols-3 lg:items-end'
                : ''
            }`}
          >
            <div>
              <label htmlFor="listino-search" className="mb-1 block text-sm font-medium text-gray-700">
                Cerca
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-3.5 h-4 w-4 text-gray-400 lg:top-3" aria-hidden="true" />
                <Input
                  id="listino-search"
                  placeholder="Nome o descrizione…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="h-11 pl-9 lg:h-10"
                />
              </div>
            </div>

            {canManageListino && (
              <div>
                <label htmlFor="listino-company-filter" className="mb-1 block text-sm font-medium text-gray-700">
                  Clinica
                </label>
                <select
                  id="listino-company-filter"
                  value={selectedCompanyId}
                  onChange={(e) => setSearchParams(e.target.value ? { companyId: e.target.value } : {})}
                  className="h-11 w-full rounded-md border border-input bg-white px-3 text-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 lg:h-10"
                >
                  <option value="">Tutte le cliniche</option>
                  {companyRows
                    .filter(c => !c.is_deleted)
                    .map(company => (
                      <option key={company._id} value={company._id}>
                        {company.nome}
                      </option>
                    ))}
                </select>
              </div>
            )}

            {canManageOrganizationListino && (
              <div>
                <label htmlFor="listino-organization-filter" className="mb-1 block text-sm font-medium text-gray-700">Listino della società</label>
                <select id="listino-organization-filter" value={selectedOrganizationId}
                  onChange={(event) => setSearchParams(event.target.value ? { organizationId: event.target.value } : {})}
                  className="h-11 w-full rounded-md border border-input bg-white px-3 text-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 lg:h-10">
                  <option value="">Tutti gli ambiti</option>
                  {organizations?.map((organization) => <option key={organization._id} value={organization._id}>{organization.nome}</option>)}
                </select>
              </div>
            )}

            {canManageListino && applicationSetupMode && (
              <div>
                <label htmlFor="listino-application-filter" className="mb-1 block text-sm font-medium text-gray-700">
                  Applicazione
                </label>
                <select
                  id="listino-application-filter"
                  value={applicationFilter}
                  onChange={(e) => setApplicationFilter(e.target.value as 'missing' | 'all')}
                  className="h-11 w-full rounded-md border border-input bg-white px-3 text-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 lg:h-10"
                >
                  <option value="missing">Solo da configurare</option>
                  <option value="all">Tutte le prestazioni</option>
                </select>
              </div>
            )}
          </div>

          {contextOrganization?.share_price_lists && (
            <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-950" role="status">
              <p className="font-semibold">Listino di {contextOrganization.nome} in uso</p>
              <p className="mt-1 max-w-3xl leading-6">
                {contextCompany?.nome ?? 'La clinica'} usa esclusivamente il listino della società per le nuove prescrizioni.
                {canManageListino
                  ? ' Qui restano salvati i listini della clinica e dei medici, sospesi finché la condivisione è attiva.'
                  : ' I listini della clinica e dei medici sono sospesi finché la condivisione è attiva.'}
              </p>
              {canManageOrganizationListino && (
                <Link className="mt-2 inline-block rounded-sm font-medium underline underline-offset-2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" to={`/listino?organizationId=${contextOrganization._id}`}>
                  Vedi listino della società
                </Link>
              )}
            </div>
          )}

          {selectedOrganization && (
            <p className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm leading-6 text-gray-700" role="status">
              {selectedOrganization.share_price_lists
                ? 'Condivisione attiva: le cliniche collegate usano esclusivamente le voci attive di questo listino. I loro listini propri e personali restano salvati e sospesi.'
                : 'Condivisione disattivata: questo listino è salvato, ma le cliniche collegate continuano a usare i propri listini e quelli personali.'}
            </p>
          )}

          {showApplicationColumn && (
            <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-950">
              <div className="flex flex-col gap-2 xl:flex-row xl:items-start xl:justify-between">
                <div>
                  <p className="font-semibold">Configurazione applicazioni listino</p>
                  <p className="mt-1 text-blue-800 tabular-nums">
                    Da configurare: <strong>{missingApplicationCount}</strong> · Configurate:{' '}
                    <strong>{(allListinoItems as any[]).length - missingApplicationCount}</strong>
                  </p>
                </div>
                <p className="max-w-2xl text-xs leading-5 text-blue-800">
                  Per dente moltiplica per gli elementi selezionati; per arcata una volta per
                  arcata; per bocca una sola volta per l’intera prescrizione.
                </p>
              </div>
              {applicationFeedback && (
                <p className="mt-3 rounded bg-white px-3 py-2" role="status">
                  {applicationFeedback}
                </p>
              )}
            </div>
          )}
        </CardHeader>

        <CardContent className="px-4 pb-4 pt-0 sm:px-6 sm:pb-6">
          {filteredItems.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 px-5 py-12 text-center">
              <List className="mx-auto h-7 w-7 text-gray-400" aria-hidden="true" />
              <h3 className="mt-3 font-medium text-gray-900">Nessuna prestazione trovata</h3>
              <p className="mx-auto mt-1 max-w-md text-sm leading-6 text-gray-500">
                {applicationSetupMode && applicationFilter === 'missing'
                  ? 'Tutte le prestazioni visualizzate sono state configurate.'
                  : searchTerm
                    ? 'Prova a modificare i criteri di ricerca.'
                    : 'Inizia creando la prima prestazione.'}
              </p>
              {!applicationSetupMode && !searchTerm && canManageListino && (
                <Link to={newItemPath} className="mt-4 inline-block w-full sm:w-auto">
                  <Button className="min-h-11 w-full sm:w-auto">
                    <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />
                    Crea prima prestazione
                  </Button>
                </Link>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full border-collapse text-left">
                <caption className="sr-only">Prestazioni configurate nel listino</caption>
                <thead className="hidden bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-500 lg:table-header-group">
                  <tr>
                    <th scope="col" className="px-3 py-3">Prestazione</th>
                    <th scope="col" className="w-48 px-3 py-3">Assegnazione</th>
                    {showApplicationColumn && (
                      <th scope="col" className="w-56 px-3 py-3">Applicazione</th>
                    )}
                    <th scope="col" className="w-40 whitespace-nowrap px-3 py-3">Prezzo</th>
                    <th scope="col" className="w-44 px-3 py-3">Stato / data</th>
                    <th scope="col" className="w-24 whitespace-nowrap px-3 py-3 text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody className="block divide-y divide-gray-200 lg:table-row-group">
                  {filteredItems.map((item: any) => {
                    const assignedUser = item.profile_id ? userLookup[item.profile_id] : undefined
                    const assignedCompany = item.company_id ? companyLookup[item.company_id] : undefined
                    const assignedOrganization = organizations?.find((organization) => organization._id === item.organization_id)
                    const hasAssignment = Boolean(assignedUser || assignedCompany || item.organization_id)
                    const canManageItem = canManageListino && (!item.organization_id || canManageOrganizationListino)
                    const isActive = item.is_active && !item.is_deleted
                    const suspended = isSuspended(item)
                    const itemName = item.nome_visualizzato || item.nome_prestazione

                    return (
                      <tr
                        key={item._id}
                        className="block bg-white transition-colors hover:bg-gray-50/70 lg:table-row"
                      >
                        <th scope="row" className="block px-4 pb-3 pt-4 text-left align-top lg:table-cell lg:px-3 lg:py-4">
                          <p className="break-words font-medium leading-5 text-gray-950">{itemName}</p>
                          {item.descrizione && (
                            <p className="mt-1 break-words text-sm leading-5 text-gray-600">
                              {item.descrizione}
                            </p>
                          )}
                        </th>

                        <td className="block px-4 pb-3 align-top lg:table-cell lg:px-3 lg:py-4">
                          <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-500 lg:hidden">
                            Assegnazione
                          </span>
                          <div className="space-y-1.5">
                            {assignedUser && (
                              <div className="flex min-w-0 items-start gap-2 text-sm text-gray-700">
                                <User className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
                                <div className="min-w-0">
                                  <p className="break-words">
                                    {assignedUser.nome} {assignedUser.cognome}
                                  </p>
                                  <Badge variant="outline" className="mt-1 max-w-full">
                                    {assignedUser.ruolo}
                                  </Badge>
                                </div>
                              </div>
                            )}
                            {assignedCompany && (
                              <div className="flex min-w-0 items-start gap-2 text-sm text-gray-700">
                                <Building className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
                                <span className="min-w-0 break-words">{assignedCompany.nome}</span>
                              </div>
                            )}
                            {item.organization_id && (
                              <div className="text-sm text-gray-700">
                                <Badge variant="outline">Società</Badge>
                                <p className="mt-1 break-words">{assignedOrganization?.nome ?? 'Listino condiviso della società'}</p>
                              </div>
                            )}
                            {!hasAssignment && <span className="text-sm text-gray-400">—</span>}
                          </div>
                        </td>

                        {showApplicationColumn && (
                          <td className="block px-4 pb-3 align-top lg:table-cell lg:px-3 lg:py-4">
                            <label
                              htmlFor={`listino-application-${item._id}`}
                              className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-500 lg:sr-only"
                            >
                              Applicazione
                            </label>
                            <Badge
                              variant="outline"
                              className={item.pricing_mode
                                ? 'mb-2 bg-green-50 text-green-800'
                                : 'mb-2 bg-red-50 text-red-800'}
                            >
                              {item.pricing_mode ? 'Configurata' : 'Da configurare'}
                            </Badge>
                            <select
                              id={`listino-application-${item._id}`}
                              disabled={!canManageItem || savingApplicationId === item._id}
                              value={item.pricing_mode || ''}
                              onChange={(e) => {
                                const value = e.target.value as PricingMode
                                if (value) void handleApplicationChange(item, value)
                              }}
                              className="h-11 w-full rounded-md border border-blue-300 bg-white px-3 text-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 lg:h-10"
                            >
                              <option value="" disabled>Seleziona applicazione</option>
                              <option value="per_tooth">Per dente</option>
                              <option value="per_arch">Per arcata</option>
                              <option value="per_mouth">Per bocca</option>
                            </select>
                            {!item.pricing_mode && (
                              <p className="mt-1 text-xs leading-5 text-blue-800">
                                Suggerimento:{' '}
                                {inferLegacyPricingMode(item) === 'per_tooth'
                                  ? 'Per dente'
                                  : 'Per arcata'}
                              </p>
                            )}
                            {savingApplicationId === item._id && (
                              <p className="mt-1 text-xs leading-5 text-blue-800">Salvataggio…</p>
                            )}
                          </td>
                        )}

                        <td className="block px-4 pb-3 align-top lg:table-cell lg:px-3 lg:py-4">
                          <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-500 lg:hidden">
                            Prezzo
                          </span>
                          <div className="space-y-2">
                            <div>
                              <p className="text-xs text-gray-500">Prezzo</p>
                              <p
                                className={`whitespace-nowrap font-semibold tabular-nums ${
                                  item.prezzo_scontato > 0 && item.prezzo_scontato < item.prezzo
                                    ? 'text-gray-400 line-through'
                                    : 'text-green-700'
                                }`}
                              >
                                {formatPrice(item.prezzo_scontato > item.prezzo ? item.prezzo_scontato : item.prezzo)}
                              </p>
                            </div>
                            {item.prezzo_scontato > 0 && item.prezzo_scontato < item.prezzo && (
                              <div>
                                <p className="text-xs text-gray-500">Prezzo scontato</p>
                                <p className="whitespace-nowrap font-semibold text-green-700 tabular-nums">
                                  {formatPrice(item.prezzo_scontato)}
                                </p>
                                <Badge variant="outline" className="mt-1 whitespace-nowrap bg-orange-50 text-orange-700">
                                  Sconto {Math.round(((item.prezzo - item.prezzo_scontato) / item.prezzo) * 100)}%
                                </Badge>
                              </div>
                            )}
                          </div>
                        </td>

                        <td className="block px-4 pb-3 align-top lg:table-cell lg:px-3 lg:py-4">
                          <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-500 lg:hidden">
                            Stato e data
                          </span>
                          <Badge className={isActive && suspended ? 'bg-amber-100 text-amber-900' : isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                            {isActive && suspended ? 'Sospeso: listino società in uso' : isActive ? 'Attiva' : 'Inattiva'}
                          </Badge>
                          {!isActive && suspended && (
                            <p className="mt-1 text-xs leading-5 text-gray-600">Listino proprio sospeso: è in uso quello della società.</p>
                          )}
                          <p className="mt-2 text-xs text-gray-500 tabular-nums">
                            Creata: {new Date(item.created_at).toLocaleDateString('it-IT')}
                          </p>
                          {item.laboratory_id && (
                            <Badge variant="outline" className="mt-2 max-w-full break-all">
                              Lab ID: {item.laboratory_id}
                            </Badge>
                          )}
                        </td>

                        <td className="block whitespace-nowrap px-4 pb-4 pt-1 align-top lg:table-cell lg:px-3 lg:py-4">
                          <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-500 lg:hidden">
                            Azioni
                          </span>
                          {(canManageItem || canManagePhases) ? (
                            <div className="flex items-center gap-2 lg:justify-end">
                              <Link to={`/listino/${item._id}/edit`} className="flex-1 lg:flex-none">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  aria-label={`${canManageItem ? 'Modifica prestazione' : 'Configura policy fasi'} ${itemName}`}
                                  title={canManageItem ? 'Modifica prestazione' : 'Configura policy fasi'}
                                  className="min-h-11 w-full gap-2 lg:h-9 lg:min-h-0 lg:w-9 lg:p-0"
                                >
                                  <Edit className="h-4 w-4" aria-hidden="true" />
                                  <span className="lg:sr-only">
                                    {canManageItem ? 'Modifica' : 'Configura fasi'}
                                  </span>
                                </Button>
                              </Link>
                              {canManageItem && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDeleteItem(item._id)}
                                  className="min-h-11 flex-1 gap-2 text-red-700 hover:text-red-800 lg:h-9 lg:min-h-0 lg:w-9 lg:flex-none lg:p-0"
                                  aria-label={`Elimina ${itemName}`}
                                  title="Elimina prestazione"
                                >
                                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                                  <span className="lg:sr-only">Elimina</span>
                                </Button>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!applicationSetupMode && !hasSearch && listinoStatus === 'CanLoadMore' && (
            <div className="mt-4 border-t border-gray-200 pt-4 text-center">
              <Button
                variant="outline"
                onClick={() => loadMoreListino(ITEMS_PER_PAGE)}
                className="min-h-11 w-full sm:w-auto sm:min-w-[200px]"
              >
                <ChevronDown className="mr-1.5 h-4 w-4" aria-hidden="true" />
                Carica altre prestazioni
              </Button>
            </div>
          )}

          {!applicationSetupMode && !hasSearch && listinoStatus === 'LoadingMore' && (
            <div className="mt-4 border-t border-gray-200 pt-4 text-center">
              <Button variant="outline" disabled className="min-h-11 w-full sm:w-auto sm:min-w-[200px]">
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />
                Caricamento…
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
